/**
 * @fileoverview Rejects a pending prospect submission, with its reason and review note.
 * @description The reviewer picks one of the backend's four reasons and writes a review note
 * (1-500 characters, required for every reason), then the review request is rejected (POST
 * /approvals/{id}/reject, with the request's current version; see useDecideApprovalRequest). The
 * bank and its registration are kept for a later resubmission. The parent mounts it only while a
 * request is being rejected, so every opening starts with a fresh form and mutation. Close is held while the request is in
 * flight, and the confirmation runs from the mutation itself (so it survives an unmount).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useApprovalRequest } from '@/features/sales/hooks/useApprovalRequest'
import { useDecideApprovalRequest } from '@/features/sales/hooks/useDecideApprovalRequest'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { requestErrorMessage } from '@/features/sales/lib/request-error.utils'
import { isStaleVersionError } from '@/features/sales/lib/stale-version.utils'
import {
  REVIEW_NOTE_MAX_LENGTH,
  rejectApprovalFormSchema,
} from '@/features/sales/schemas/reject-approval-form.schema'
import Label from '@/shared/components/atoms/Label'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import StaleVersionNotice from './StaleVersionNotice'

import type { RejectApprovalFormValues } from '@/features/sales/schemas/reject-approval-form.schema'
import type {
  ApprovalListItem,
  ApprovalRejectionReason,
} from '@/features/sales/types/approval.types'
import type { FormEvent } from 'react'

const REASONS: ApprovalRejectionReason[] = [
  'INVALID_DETAILS',
  'DUPLICATE_BANK',
  'OUTSIDE_BUSINESS_SCOPE',
  'OTHER',
]

const FORM_ID = 'reject-prospect-form'

interface RejectProspectDialogProps {
  request: ApprovalListItem
  onClose: () => void
  onRejected: (request: ApprovalListItem) => void
}

/**
 * @component
 * @param {RejectProspectDialogProps} props - The request and the handlers.
 * @param {ApprovalListItem} props.request - The pending review request.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(request: ApprovalListItem) => void} props.onRejected - Called once it's rejected.
 */
function RejectProspectDialog({ request, onClose, onRejected }: RejectProspectDialogProps) {
  const { t } = useTranslation()
  const approval = useApprovalRequest(request.approvalRequestId)
  const decide = useDecideApprovalRequest({
    /**
     * @description Reports the rejection to the parent, even if this dialog has unmounted.
     */
    onSuccess: () => onRejected(request),
  })
  const close = useGuardedClose(onClose, decide.isPending)
  const refreshSales = useRefreshSalesQueries()
  const loaded = approval.data
  const isStale = decide.isError && isStaleVersionError(decide.error)
  const isReady = approval.isSuccess && !approval.isFetching && !isStale

  /**
   * @description Clears a stale refusal and reloads the request's latest state for review.
   */
  const onRefresh = () => {
    decide.reset()
    refreshSales()
  }
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RejectApprovalFormValues>({
    resolver: zodResolver(rejectApprovalFormSchema),
    defaultValues: { reviewNote: '' },
  })

  /**
   * @description Rejects the request at the version shown, with the validated reason and note.
   * @param {RejectApprovalFormValues} values - The form's values.
   */
  const onValid = (values: RejectApprovalFormValues) => {
    if (!loaded) {
      return
    }
    decide.mutate({
      approvalRequestId: request.approvalRequestId,
      approvalVersion: loaded.approvalVersion,
      decision: 'reject',
      rejection: values,
    })
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="reject-prospect-dialog"
      title={t('sales.pipeline.reject.title')}
      subtitle={request.bankName}
    >
      <form
        id={FORM_ID}
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
        data-testid="reject-prospect-form"
      >
        <SelectField
          id="rejectReason"
          data-testid="reject-prospect-reason-select"
          label={t('sales.pipeline.reject.reasonLabel')}
          options={[
            { value: '', label: t('sales.pipeline.reject.reasonPlaceholder') },
            ...REASONS.map((reason) => ({
              value: reason,
              label: t(`sales.pipeline.reject.reasons.${reason}`),
            })),
          ]}
          error={errors.reasonCode?.message ? t(errors.reasonCode.message) : undefined}
          {...register('reasonCode')}
        />
        <div>
          <Label htmlFor="rejectNote">{t('sales.pipeline.reject.noteLabel')}</Label>
          <textarea
            id="rejectNote"
            data-testid="reject-prospect-note-input"
            rows={3}
            maxLength={REVIEW_NOTE_MAX_LENGTH}
            placeholder={t('sales.pipeline.reject.notePlaceholder')}
            aria-invalid={Boolean(errors.reviewNote)}
            className="mt-2 w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink focus:ring-2 focus:ring-primary/20 focus:outline-none"
            {...register('reviewNote')}
          />
          {errors.reviewNote?.message && (
            <p className="mt-1.5 text-[13px] text-red-600" data-testid="reject-prospect-note-error">
              {t(errors.reviewNote.message)}
            </p>
          )}
        </div>
        {approval.isError && (
          <p
            role="alert"
            className="text-[13px] text-danger"
            data-testid="reject-prospect-load-error"
          >
            {requestErrorMessage(approval.error, t)}
          </p>
        )}
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={approval.isFetching} />}
        {decide.isError && !isStale && (
          <p role="alert" className="text-[13px] text-danger" data-testid="reject-prospect-error">
            {requestErrorMessage(decide.error, t)}
          </p>
        )}
        <DialogActions
          testIdPrefix="reject-prospect"
          cancelLabel={t('sales.pipeline.approve.cancel')}
          confirmLabel={t('sales.pipeline.reject.submit')}
          onCancel={close}
          confirmType="submit"
          isLoading={decide.isPending}
          disabled={!isReady}
        />
      </form>
    </Modal>
  )
}

export default RejectProspectDialog
